import type { HRContractTemplate, HREmployee, HREmployeeContract } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

// ── Schemas ──────────────────────────────────────────────────────────────────

const newContractSchema = z.object({
  employeeId: z.number().min(1, 'Employee is required'),
  templateId: z.number().optional(),
  title: z.string().min(1, 'Title is required'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  notes: z.string().optional(),
})

const templateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['employment', 'nda', 'amendment', 'termination']),
  description: z.string().optional(),
  body: z.string().min(1, 'Body is required'),
  isActive: z.boolean().optional(),
})

type NewContractFormValues = z.infer<typeof newContractSchema>
type TemplateFormValues = z.infer<typeof templateSchema>

export const Route = createFileRoute('/hr/contracts/')({
  component: ContractsPage,
})

function ContractsPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <ContractsContent />
    </ModuleGuard>
  )
}

function ContractsContent() {
  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Contracts</h1>
        <p className="text-muted-foreground">Manage employee contracts and templates</p>
      </div>

      <Tabs defaultValue="contracts">
        <TabsList className="mb-4">
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="contracts">
          <ContractsList />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesList />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ── Contracts List ────────────────────────────────────────────────────────────

function ContractsList() {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchFilter, setSearchFilter] = useState('')
  const [isFormOpen, setIsFormOpen] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  type ContractStatus = 'draft' | 'active' | 'expired' | 'terminated'

  const { data: contractsData, isLoading } = useQuery(
    orpc.hr.listContracts.queryOptions({
      input: {
        status: statusFilter === 'all' ? undefined : (statusFilter as ContractStatus),
        page: 1,
        limit: 100,
      },
    }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const { data: templatesData } = useQuery(
    orpc.hr.listContractTemplates.queryOptions({ input: { activeOnly: true } }),
  )

  const contracts = (contractsData?.data ?? []) as (HREmployeeContract & {
    employee?: HREmployee
    template?: HRContractTemplate
  })[]
  const employees = (employeesData?.data ?? []) as HREmployee[]
  const templates = (templatesData ?? []) as HRContractTemplate[]

  const createMutation = useMutation(
    orpc.hr.createContract.mutationOptions({
      onSuccess: () => {
        toast.success('Contract created')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listContracts']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  const filteredContracts = searchFilter
    ? contracts.filter((c) => {
        const empName = getEmployeeName(c.employeeId).toLowerCase()
        return empName.includes(searchFilter.toLowerCase()) || c.title.toLowerCase().includes(searchFilter.toLowerCase())
      })
    : contracts

  const handleCreateContract = async (data: NewContractFormValues) => {
    await createMutation.mutateAsync(data)
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4">
              <Input
                placeholder="Search by employee or title..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="w-[260px]"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setIsFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Contract
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No contracts found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContracts.map((contract) => (
                  <TableRow
                    key={contract.id}
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: `/hr/contracts/${contract.id}` })}
                  >
                    <TableCell className="font-medium">{contract.title}</TableCell>
                    <TableCell>{getEmployeeName(contract.employeeId)}</TableCell>
                    <TableCell>
                      {contract.startDate
                        ? new Date(contract.startDate).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {contract.endDate
                        ? new Date(contract.endDate).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={contract.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          void navigate({ to: `/hr/contracts/${contract.id}` })
                        }}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewContractDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employees={employees}
        templates={templates}
        onSubmit={handleCreateContract}
      />
    </>
  )
}

interface NewContractDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: HREmployee[]
  templates: HRContractTemplate[]
  onSubmit: (data: NewContractFormValues) => Promise<void>
}

function NewContractDialog({
  open,
  onOpenChange,
  employees,
  templates,
  onSubmit,
}: NewContractDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<NewContractFormValues>({
    resolver: zodResolver(newContractSchema),
    defaultValues: {
      title: '',
      startDate: '',
      endDate: '',
      notes: '',
    },
  })

  const handleSubmit = async (data: NewContractFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>New Contract</DialogTitle>
          <DialogDescription>Create a new employee contract</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="templateId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v === 'none' ? undefined : Number(v))}
                    value={field.value?.toString() ?? 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select template (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No template</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id.toString()}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="Employment Contract" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional notes..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Contract'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

// ── Templates List ────────────────────────────────────────────────────────────

const templateTypeColors: Record<string, string> = {
  employment: 'bg-blue-100 text-blue-800',
  nda: 'bg-purple-100 text-purple-800',
  amendment: 'bg-yellow-100 text-yellow-800',
  termination: 'bg-red-100 text-red-800',
}

function TemplatesList() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<HRContractTemplate | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  })

  const queryClient = useQueryClient()

  const { data: templatesData, isLoading } = useQuery(
    orpc.hr.listContractTemplates.queryOptions({ input: {} }),
  )

  const templates = (templatesData ?? []) as HRContractTemplate[]

  const createMutation = useMutation(
    orpc.hr.createContractTemplate.mutationOptions({
      onSuccess: () => {
        toast.success('Template created')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listContractTemplates']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateMutation = useMutation(
    orpc.hr.updateContractTemplate.mutationOptions({
      onSuccess: () => {
        toast.success('Template updated')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listContractTemplates']] })
        setIsFormOpen(false)
        setEditingTemplate(null)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteContractTemplate.mutationOptions({
      onSuccess: () => {
        toast.success('Template deleted')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listContractTemplates']] })
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const handleEdit = (template: HRContractTemplate) => {
    setEditingTemplate(template)
    setIsFormOpen(true)
  }

  const handleSubmit = async (data: TemplateFormValues) => {
    if (editingTemplate) {
      await updateMutation.mutateAsync({ id: editingTemplate.id, ...data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setEditingTemplate(null)
                setIsFormOpen(true)
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No templates found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell>
                      <Badge
                        className={`${templateTypeColors[template.type] ?? ''} capitalize`}
                        variant="outline"
                      >
                        {template.type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={template.isActive ? 'active' : 'inactive'} />
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {template.description ?? '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(template)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, id: template.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TemplateFormDialog
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open)
          if (!open) setEditingTemplate(null)
        }}
        template={editingTemplate}
        onSubmit={handleSubmit}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => deleteDialog.id && deleteMutation.mutate({ id: deleteDialog.id })}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

interface TemplateFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: HRContractTemplate | null
  onSubmit: (data: TemplateFormValues) => Promise<void>
}

function TemplateFormDialog({ open, onOpenChange, template, onSubmit }: TemplateFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: template?.name ?? '',
      type: (template?.type ?? 'employment') as TemplateFormValues['type'],
      description: template?.description ?? '',
      body: template?.body ?? '',
      isActive: template?.isActive ?? true,
    },
  })

  const handleSubmit = async (data: TemplateFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'New Template'}</DialogTitle>
          <DialogDescription>
            {template ? 'Update contract template' : 'Create a new contract template'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Standard Employment Contract" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v as TemplateFormValues['type'])}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="employment">Employment</SelectItem>
                      <SelectItem value="nda">NDA</SelectItem>
                      <SelectItem value="amendment">Amendment</SelectItem>
                      <SelectItem value="termination">Termination</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Template description..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Body *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Contract body content (HTML or Markdown)..."
                      rows={10}
                      className="font-mono text-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
